import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Box, CircularProgress, Container } from '@mui/material';
import { useAuth } from '../contexts/AuthContext';

/**
 * ProtectedRoute component props
 */
interface ProtectedRouteProps {
  /** Child components to render when authenticated */
  children: React.ReactNode;
  /** Optional redirect path (defaults to /login) */
  redirectTo?: string;
}

/**
 * Protected Route Component
 *
 * Wrapper component that protects routes requiring authentication
 * Redirects unauthenticated users to login page
 * Shows loading spinner during authentication state initialization
 *
 * Usage:
 *   <Route
 *     path="/dashboard"
 *     element={
 *       <ProtectedRoute>
 *         <Dashboard />
 *       </ProtectedRoute>
 *     }
 *   />
 */
export default function ProtectedRoute({
  children,
  redirectTo = '/login',
}: ProtectedRouteProps): React.ReactElement {
  const { user, loading } = useAuth();
  const location = useLocation();

  // Show loading spinner while checking authentication state
  if (loading) {
    return (
      <Container>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '100vh',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          <CircularProgress size={48} />
          <Box sx={{ color: 'text.secondary' }}>Loading...</Box>
        </Box>
      </Container>
    );
  }

  // Redirect to login if not authenticated
  // Preserve the intended destination in state for post-login redirect
  if (!user) {
    return <Navigate to={redirectTo} state={{ from: location }} replace />;
  }

  // User is authenticated, render protected content
  return <>{children}</>;
}
